import { createContext, useContext, useEffect, useState } from "react";
import api from "../services/api";

// Auth Context
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      fetchUserProfile();
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchUserProfile = async () => {
    try {
      const { data } = await api.get("/user/profile");
      if (data.success) {
        setUser(data.data.user);
      }
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      localStorage.removeItem("token");
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.success) {
        if (data.data.requiresMfa) {
          // Hold the token temporarily — do NOT store in localStorage until MFA is verified
          return { success: true, requiresMfa: true, mfaToken: data.data.token };
        }
        localStorage.setItem("token", data.data.token);
        setUser(data.data.user);
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || "Login failed",
      };
    }
  };

  const completeMfaLogin = async (mfaToken) => {
    localStorage.setItem("token", mfaToken);
    await fetchUserProfile();
  };

  const googleLogin = async (credential) => {
    try {
      const { data } = await api.post("/auth/google", { credential });
      if (data.success) {
        localStorage.setItem("token", data.data.token);
        setUser(data.data.user);
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error("Google Login error:", error);
      return {
        success: false,
        error: error.response?.data?.error || "Google Login failed",
      };
    }
  };

  const register = async (userData) => {
    try {
      const { data } = await api.post("/auth/register", userData);
      if (data.success) {
        localStorage.setItem("token", data.data.token);
        setUser(data.data.user);
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error("Registration error:", error);
      return {
        success: false,
        error:
          error.response?.data?.error || error.message || "Registration failed",
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, googleLogin, logout, completeMfaLogin, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
